import { Menu, Text } from '@mantine/core';
import { Icon } from '@uag/react-core';
import { useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Vector2 } from 'three';
import { useShallow } from 'zustand/shallow';

import { ShortcutAction } from 'app/modules/wallforming/components/scenes/toolbar/types/shortcutAction';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const ndcPosition = new Vector2();

export const Paste = () => {
    const { t } = useTranslation();

    const { clipboard, mainWebGLRendererRef, pasteArticleInstances } = usePlacementStore(
        useShallow((state) => ({
            clipboard: state.clipboard,
            mainWebGLRendererRef: state.mainWebGLRendererRef,
            pasteArticleInstances: state.pasteArticleInstances
        }))
    );

    const lastPointerEventRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    const handleOnPointerMove = useCallback((e: PointerEvent) => {
        lastPointerEventRef.current = { x: e.clientX, y: e.clientY };
    }, []);

    useEffect(() => {
        window.addEventListener('pointermove', handleOnPointerMove);
        return () => window.removeEventListener('pointermove', handleOnPointerMove);
    }, [handleOnPointerMove]);

    const handleOnPaste = useCallback(() => {
        const canvas = mainWebGLRendererRef.current?.domElement;
        if (canvas) {
            const rect = canvas.getBoundingClientRect();
            ndcPosition.set(
                ((lastPointerEventRef.current.x - rect.left) / rect.width) * 2 - 1,
                -((lastPointerEventRef.current.y - rect.top) / rect.height) * 2 + 1
            );
            pasteArticleInstances(ndcPosition);
        }
    }, [mainWebGLRendererRef, pasteArticleInstances]);

    return (
        <Menu.Item
            disabled={clipboard.length === 0}
            leftSection={
                <Icon className="action-menu-entry-decoration action-menu-entry-decoration-icon" icon="content_paste" />
            }
            rightSection={
                <Text className="action-menu-entry-decoration-text text-right text-[13px]">{ShortcutAction.Paste}</Text>
            }
            onClick={handleOnPaste}
        >
            {t('paste')}
        </Menu.Item>
    );
};
